import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Calendar,
  Building2,
  MapPin,
  Truck,
  Box,
  Plane,
  Ship,
  Thermometer,
  Layers,
  FileText,
  Anchor,
  HelpCircle
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import CustomerCombobox from '../../components/CustomerCombobox';
import MasterCombobox from '../../components/MasterCombobox';

const BookingRequestForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const today = new Date().toISOString().split('T')[0];

  const loggedInName = (() => {
    try {
      const p = JSON.parse(localStorage.getItem('user_profile') || '{}');
      return p.full_name || p.email || '';
    } catch {
      return '';
    }
  })();

  // Main shared form state
  const [form, setForm] = useState({
    company_id: '',
    inquiry_date: today,
    customer_id: '',
    customer_name: '',
    customer_branch_id: '',
    customer_branch_name: '',
    branch_address: '',
    booking_type: 'Online',
    from_location_id: '',
    from_location_name: '',
    to_location_id: '',
    to_location_name: '',
    load_type: '',
    vehicle_type_id: '',
    vehicle_type_name: '',
    no_of_vehicles: 1,
    trip_type: '',
    vehicle_size: '',
    weight: '',
    gross_weight: '',
    packages: '',
    dimensions: '',
    dimensions_unit: 'CM',
    remarks: '',
    booking_date: today,
    employee_name: loggedInName,
    inquiry_type: 'Direct'
  });

  // Freight Forwarding specific fields (docket aligned)
  const [ffData, setFfData] = useState({
    inward_outward: 'Outward',
    shipper_id: '',
    shipper_name: '',
    consignee_id: '',
    consignee_name: '',
    mode_of_transport: 'AIR', // 'AIR' | 'SEA'
    service_type: 'Door To Door',
    cargo_type: 'General',
    commercial_type: 'Commercial',
    cargo_ready_date: today,
    temperature_requirement: '',
    incoterms: 'FOB',
    carrier_id: '',
    carrier_name: '',
    target_etd: '',
    target_eta: '',
    sea_shipment_type: 'FCL', // 'FCL' | 'LCL'
  });

  // Sea FCL Containers
  const [containerItems, setContainerItems] = useState([
    { id: 1, container_type: "40' High Cube", no_of_containers: 1 }
  ]);

  // Multiple package rows (shared across logistics & freight forwarding)
  const [packageItems, setPackageItems] = useState([
    { id: 1, package_type: 'Boxes', packages: '', gross_weight: '', length: '', width: '', height: '', dimensions: '', unit: 'CM' }
  ]);

  // Multi-truck capacities (domestic logistics)
  const [truckSizes, setTruckSizes] = useState([
    { id: 1, label: 'Truck 1', size: '' }
  ]);

  // Selected company object & business mode detection
  const selectedCompany = useMemo(() => {
    return companies.find(c => c.id === selectedCompanyId) || null;
  }, [companies, selectedCompanyId]);

  const isFreightForwarding = useMemo(() => {
    if (!selectedCompany) return false;
    let modes = [];
    try {
      modes = Array.isArray(selectedCompany.business_modes)
        ? selectedCompany.business_modes
        : JSON.parse(selectedCompany.business_modes || '[]');
    } catch {
      modes = [];
    }
    return (
      selectedCompany.default_mode === 'FREIGHT_FORWARDING' ||
      modes.includes('FREIGHT_FORWARDING') ||
      selectedCompany.code === 'SILLP'
    );
  }, [selectedCompany]);

  const isLogistics = !isFreightForwarding;

  // Load masters & companies
  useEffect(() => {
    const h = getAuthHeaders();
    const fetches = [
      fetch(`${backendUrl}/api/customers?limit=500`, { headers: h })
        .then(r => r.ok ? r.json() : null)
        .then(d => d ? (d.items || d || []) : []),
      fetch(`${backendUrl}/api/locations?limit=500`, { headers: h })
        .then(r => r.ok ? r.json() : null)
        .then(d => d ? (d.items || d || []) : []),
      fetch(`${backendUrl}/api/vehicle-types?limit=500`, { headers: h })
        .then(r => r.ok ? r.json() : null)
        .then(d => d ? (d.items || d || []) : []),
      fetch(`${backendUrl}/api/companies`, { headers: h })
        .then(r => r.ok ? r.json() : null)
        .then(d => d ? (d.items || d || []) : []),
      fetch(`${backendUrl}/api/carriers?is_active=true`, { headers: h })
        .then(r => r.ok ? r.json() : null)
        .then(d => d ? (d.items || d || []) : [])
    ];

    Promise.all(fetches).then(([cust, locs, vtypes, comps, carrs]) => {
      setCustomers(cust);
      setLocations(locs.map(l => ({
        id: l.id,
        name: l.name || l.city_name || l.city || l.code,
        state: l.state,
        code: l.code,
        type: l.type
      })));
      const rawVt = Array.isArray(vtypes) ? vtypes : (vtypes?.items || []);
      setVehicleTypes(rawVt.map(v => ({
        ...v,
        name: v.type_name || v.name || '',
        type_name: v.type_name || v.name || '',
        sizes: v.sizes || []
      })));
      setCompanies(comps);
      setCarriers(carrs);

      // Default company selection if not editing
      if (!isEdit && comps.length > 0) {
        const defaultComp = comps.find(c => c.is_default) || comps[0];
        setSelectedCompanyId(defaultComp.id);
        setForm(f => ({ ...f, company_id: defaultComp.id }));
      }
    }).catch(err => {
      console.error('Error fetching masters:', err);
    });
  }, [backendUrl, getAuthHeaders, isEdit]);

  // Customer branches
  const fetchBranches = useCallback(async (customerId) => {
    if (!customerId) {
      setBranches([]);
      return;
    }
    try {
      const res = await fetch(`${backendUrl}/api/customers/${customerId}/branches`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const branchList = Array.isArray(data) ? data : (data.items || []);
        setBranches(branchList);
        return branchList;
      }
    } catch (e) {
      console.error('Failed to load branches:', e);
    }
    setBranches([]);
    return [];
  }, [backendUrl, getAuthHeaders]);

  // Load existing booking if edit
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch(`${backendUrl}/api/booking-requests/${id}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        if (!data || data.detail) return;
        setForm({
          company_id: data.company_id || '',
          inquiry_date: data.inquiry_date || today,
          customer_id: data.customer_id || '',
          customer_name: data.customer_name || '',
          customer_branch_id: data.customer_branch_id || '',
          customer_branch_name: data.customer_branch_name || '',
          branch_address: data.branch_address || '',
          booking_type: data.booking_type || 'Online',
          from_location_id: data.from_location_id || '',
          from_location_name: data.from_location_name || '',
          to_location_id: data.to_location_id || '',
          to_location_name: data.to_location_name || '',
          load_type: data.load_type || '',
          vehicle_type_id: data.vehicle_type_id || '',
          vehicle_type_name: data.vehicle_type_name || '',
          no_of_vehicles: data.no_of_vehicles || 1,
          trip_type: data.trip_type || '',
          vehicle_size: data.vehicle_size || '',
          weight: data.weight || '',
          gross_weight: data.gross_weight || '',
          packages: data.packages || '',
          dimensions: data.dimensions || '',
          dimensions_unit: data.dimensions_unit || 'CM',
          remarks: data.remarks || '',
          booking_date: data.booking_date || today,
          employee_name: data.employee_name || loggedInName,
          inquiry_type: data.inquiry_type || 'Direct'
        });

        if (data.company_id) {
          setSelectedCompanyId(data.company_id);
        }

        if (data.customer_id) {
          fetchBranches(data.customer_id);
        }

        // Restore Freight Forwarding extra details if present
        if (data.extra_details) {
          setFfData(prev => ({
            ...prev,
            ...data.extra_details,
            service_type: data.service_type || data.extra_details.service_type || prev.service_type,
            incoterms: data.incoterms || data.extra_details.incoterms || prev.incoterms,
          }));
          if (Array.isArray(data.extra_details.container_items) && data.extra_details.container_items.length > 0) {
            setContainerItems(data.extra_details.container_items);
          }
        } else if (data.category === 'FREIGHT_FORWARDING') {
          setFfData(prev => ({
            ...prev,
            service_type: data.service_type || prev.service_type,
            incoterms: data.incoterms || prev.incoterms,
          }));
        }

        // Restore package items
        if (Array.isArray(data.package_items) && data.package_items.length > 0) {
          setPackageItems(data.package_items);
        }
      })
      .catch(err => console.error('Failed to load booking:', err))
      .finally(() => setLoading(false));
  }, [id, backendUrl, getAuthHeaders, today, loggedInName, fetchBranches]);

  // Package row helpers
  const handleAddPackageRow = () => {
    setPackageItems(prev => [
      ...prev,
      { id: Date.now(), package_type: 'Boxes', packages: '', gross_weight: '', length: '', width: '', height: '', dimensions: '', unit: 'CM' }
    ]);
  };

  const handleRemovePackageRow = (index) => {
    if (packageItems.length <= 1) return;
    setPackageItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdatePackageItem = (index, field, value) => {
    setPackageItems(prev => {
      const updated = [...prev];
      const item = { ...updated[index], [field]: value };

      // Auto update dimensions string if L, W, H change
      if (['length', 'width', 'height'].includes(field)) {
        const l = field === 'length' ? value : item.length;
        const w = field === 'width' ? value : item.width;
        const h = field === 'height' ? value : item.height;
        if (l || w || h) {
          item.dimensions = `${l || 0} x ${w || 0} x ${h || 0}`;
        }
      }
      updated[index] = item;
      return updated;
    });
  };

  // Calculations for packages & cargo
  const totalPackages = useMemo(() => {
    return packageItems.reduce((sum, item) => sum + (parseInt(item.packages, 10) || 0), 0);
  }, [packageItems]);

  const totalGrossWeight = useMemo(() => {
    return packageItems.reduce((sum, item) => sum + (parseFloat(item.gross_weight) || 0), 0);
  }, [packageItems]);

  // Total Volume CBM
  const totalCBM = useMemo(() => {
    return packageItems.reduce((sum, item) => {
      const pkgs = parseInt(item.packages, 10) || 1;
      const l = parseFloat(item.length) || 0;
      const w = parseFloat(item.width) || 0;
      const h = parseFloat(item.height) || 0;
      if (!l || !w || !h) return sum;

      const unit = item.unit || 'CM';
      let cbm = 0;
      if (unit === 'Inches' || unit === 'IN') {
        cbm = (l * w * h * pkgs) / 61023.7;
      } else {
        cbm = (l * w * h * pkgs) / 1000000;
      }
      return sum + cbm;
    }, 0);
  }, [packageItems]);

  // Total Volumetric Weight (Kg)
  const totalVolumetricWeight = useMemo(() => {
    return packageItems.reduce((sum, item) => {
      const pkgs = parseInt(item.packages, 10) || 1;
      const l = parseFloat(item.length) || 0;
      const w = parseFloat(item.width) || 0;
      const h = parseFloat(item.height) || 0;
      if (!l || !w || !h) return sum;

      const unit = item.unit || 'CM';
      let vol = 0;
      if (ffData.mode_of_transport === 'AIR') {
        // Air freight standard ratio: 1:6000 (CM)
        if (unit === 'Inches' || unit === 'IN') {
          vol = (l * w * h * pkgs) / 366;
        } else {
          vol = (l * w * h * pkgs) / 6000;
        }
      } else {
        // Sea freight ratio: 1 CBM = 1000 Kg
        let cbm = 0;
        if (unit === 'Inches' || unit === 'IN') {
          cbm = (l * w * h * pkgs) / 61023.7;
        } else {
          cbm = (l * w * h * pkgs) / 1000000;
        }
        vol = cbm * 1000;
      }
      return sum + vol;
    }, 0);
  }, [packageItems, ffData.mode_of_transport]);

  const chargeableWeight = useMemo(() => {
    return Math.max(totalGrossWeight, totalVolumetricWeight);
  }, [totalGrossWeight, totalVolumetricWeight]);

  // Multi-truck capacities for domestic logistics
  const handleNumTrucksChange = (num) => {
    const n = Math.max(1, parseInt(num, 10) || 1);
    setForm(f => ({ ...f, no_of_vehicles: n }));
    setTruckSizes(prev => {
      const updated = [];
      for (let i = 0; i < n; i++) {
        updated.push(prev[i] || { id: i + 1, label: `Truck ${i + 1}`, size: '' });
      }
      return updated;
    });
  };

  const handleTruckSizeChange = (idx, value) => {
    setTruckSizes(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], size: value };
      return updated;
    });
  };

  // Container rows for Sea FCL
  // Available Vehicle Sizes from Master (filtered by vehicle_type if selected)
  const availableVehicleSizes = useMemo(() => {
    if (vehicleTypes && vehicleTypes.length > 0) {
      if (form.vehicle_type_id || form.vehicle_type_name) {
        const matched = vehicleTypes.find(vt =>
          (form.vehicle_type_id && String(vt.id) === String(form.vehicle_type_id)) ||
          (form.vehicle_type_name && (vt.type_name || vt.name || '').toUpperCase() === (form.vehicle_type_name || '').toUpperCase())
        );
        if (matched && matched.sizes && matched.sizes.length > 0) {
          return matched.sizes.map(s => typeof s === 'string' ? s : s.size_name).filter(Boolean);
        }
      }
      const all = [];
      vehicleTypes.forEach(vt => {
        (vt.sizes || []).forEach(s => {
          const sName = typeof s === 'string' ? s : s.size_name;
          if (sName && !all.includes(sName)) all.push(sName);
        });
      });
      if (all.length > 0) return all;
    }
    return ['14 FT', '17 FT', '19 FT', '20 FT', '22 FT', '24 FT', '32 FT Single Axle', '32 FT Multi Axle', '40 FT Trailer', 'Taurus 16 Ton', 'Taurus 21 Ton'];
  }, [vehicleTypes, form.vehicle_type_id, form.vehicle_type_name]);

  const handleVehicleTypeChange = (selectedVal) => {
    const vt = vehicleTypes.find(v => String(v.id) === String(selectedVal) || (v.type_name || v.name) === selectedVal);
    const typeId = vt ? vt.id : selectedVal;
    const typeName = vt ? (vt.type_name || vt.name) : selectedVal;

    let newSize = form.vehicle_size;
    if (vt && vt.sizes && vt.sizes.length > 0) {
      const typeSizes = vt.sizes.map(s => typeof s === 'string' ? s : s.size_name);
      if (!typeSizes.includes(newSize)) {
        newSize = '';
      }
    }

    setForm(f => ({
      ...f,
      vehicle_type_id: typeId,
      vehicle_type_name: typeName,
      vehicle_size: newSize
    }));
  };

  const handleAddContainerRow = () => {
    setContainerItems(prev => [
      ...prev,
      { id: Date.now(), container_type: "40' High Cube", no_of_containers: 1 }
    ]);
  };

  const handleRemoveContainerRow = (index) => {
    if (containerItems.length <= 1) return;
    setContainerItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateContainerItem = (index, field, value) => {
    setContainerItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Customer change
  const handleCustomerChange = (custOrId, maybeObj) => {
    const cust = (typeof custOrId === 'object' && custOrId !== null)
      ? custOrId
      : (typeof maybeObj === 'object' && maybeObj !== null
          ? maybeObj
          : customers.find(x => String(x.id) === String(custOrId)));

    if (!cust) {
      setForm(f => ({
        ...f,
        customer_id: '',
        customer_name: '',
        customer_branch_id: '',
        customer_branch_name: '',
        branch_address: ''
      }));
      setBranches([]);
      return;
    }

    setForm(f => ({
      ...f,
      customer_id: cust.id,
      customer_name: cust.business_name || cust.name || cust.company_name || ''
    }));

    fetchBranches(cust.id).then(branchList => {
      if (branchList && branchList.length > 0) {
        const ho = branchList.find(b => b.is_head_office) || branchList[0];
        setForm(f => ({
          ...f,
          customer_branch_id: ho.id,
          customer_branch_name: ho.branch_nick_name || ho.branch_name || ho.city || 'Main Branch',
          branch_address: [ho.address_line_1, ho.city, ho.state, ho.pincode].filter(Boolean).join(', ')
        }));
      } else {
        setForm(f => ({
          ...f,
          customer_branch_id: '',
          customer_branch_name: '',
          branch_address: ''
        }));
      }
    });
  };

  const handleBranchSelect = (branchId) => {
    const b = branches.find(x => String(x.id) === String(branchId));
    if (b) {
      setForm(f => ({
        ...f,
        customer_branch_id: b.id,
        customer_branch_name: b.branch_nick_name || b.branch_name || b.city || 'Branch',
        branch_address: [b.address_line_1, b.address_line_2, b.city, b.state, b.pincode].filter(Boolean).join(', ')
      }));
    } else {
      setForm(f => ({ ...f, customer_branch_id: branchId || '', customer_branch_name: '', branch_address: '' }));
    }
  };

  // Form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!form.customer_id) {
      toast({ title: 'Validation', description: 'Please select a Customer', variant: 'destructive' });
      return;
    }
    if (!form.from_location_name || !form.to_location_name) {
      toast({ title: 'Validation', description: 'Please specify both Origin and Destination', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (selectedCompanyId) payload.company_id = selectedCompanyId;

      if (isLogistics) {
        payload.freight_term = form.freight_term || 'PAID';
        payload.incoterms = form.freight_term || 'PAID';
      }
      if (isFreightForwarding) {
        // Freight Forwarding domain
        payload.category = 'FREIGHT_FORWARDING';
        payload.service_type = ffData.service_type;
        payload.incoterms = ffData.incoterms;
        payload.load_type = ffData.cargo_type;

        // Extra details for 1-to-1 docket conversion
        payload.extra_details = {
          ...ffData,
          container_items: containerItems,
          total_cbm: parseFloat(totalCBM.toFixed(3)),
          total_volumetric_weight: parseFloat(totalVolumetricWeight.toFixed(2)),
          chargeable_weight: parseFloat(chargeableWeight.toFixed(2)),
          shipper_name: ffData.shipper_name || form.customer_name,
          consignee_name: ffData.consignee_name || '',
          carrier_name: ffData.carrier_name || '',
        };
      } else {
        // Domestic Logistics domain
        payload.category = 'LOGISTICS';
        payload.service_type = 'FTL / PTL';

        // Integrate multi-truck sizes
        if (form.no_of_vehicles > 1) {
          const filledSizes = truckSizes.map(t => t.size).filter(Boolean);
          if (filledSizes.length > 0) {
            payload.vehicle_size = filledSizes.join(', ');
          }
        }
      }

      // Multi-row package items integration
      const validItems = packageItems.filter(p => p.packages || p.gross_weight || p.dimensions || p.length);
      if (validItems.length > 0) {
        payload.package_items = validItems;
        if (totalPackages > 0) payload.packages = String(totalPackages);
        if (totalGrossWeight > 0) {
          payload.gross_weight = String(totalGrossWeight);
          payload.weight = String(totalGrossWeight);
        }
        const dimSummary = validItems
          .filter(p => p.dimensions || (p.length && p.width && p.height))
          .map(p => `${p.packages ? p.packages + ' pkgs: ' : ''}${p.dimensions || `${p.length}x${p.width}x${p.height}`} ${p.unit}`)
          .join('; ');
        if (dimSummary) payload.dimensions = dimSummary;
      }

      // Clean empty strings to avoid Pydantic 422
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') {
          payload[key] = null;
        }
      });

      const url = isEdit ? `${backendUrl}/api/booking-requests/${id}` : `${backendUrl}/api/booking-requests`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save booking request');
      }

      const saved = await res.json();
      toast({
        title: isEdit ? 'Booking Updated' : 'Booking Created',
        description: `Booking Inquiry ${saved.inquiry_number || ''} saved successfully.`
      });
      navigate('/booking');
    } catch (err) {
      console.error('Submit error:', err);
      toast({
        title: 'Error Saving Booking',
        description: err.message || 'An error occurred',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const branchOptions = useMemo(() => {
    return branches.map(b => ({
      id: b.id,
      name: `${b.branch_nick_name || b.branch_name || b.city || 'Branch'}${b.city ? ` (${b.city})` : ''}`,
      address: [b.address_line_1, b.city, b.state].filter(Boolean).join(', ')
    }));
  }, [branches]);

  const locationOptions = useMemo(() => {
    return locations.map(l => ({
      id: l.id,
      name: `${l.name}${l.state ? ` (${l.state})` : ''}${l.code ? ` - ${l.code}` : ''}`
    }));
  }, [locations]);

  const carrierOptions = useMemo(() => {
    return carriers.map(c => ({
      id: c.id,
      name: `${c.carrier_name || c.name}${c.carrier_code ? ` (${c.carrier_code})` : ''}`
    }));
  }, [carriers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-sm text-gray-500 font-ibm">Loading booking details...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-16">
      {/* ─── Header & Actions ─── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/booking')}
            className="hover:bg-slate-100 rounded-lg text-slate-600 cursor-pointer"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold text-text-dark font-manrope">
                {isEdit ? `Edit Booking / Inquiry (${form.inquiry_number || id})` : 'New Booking / Inquiry'}
              </h1>
              <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider font-ibm ${
                isFreightForwarding ? 'bg-purple-100 text-purple-800 border border-purple-200' : 'bg-blue-100 text-blue-800 border border-blue-200'
              }`}>
                {isFreightForwarding ? 'Freight Forwarding Mode' : 'Domestic Logistics Mode'}
              </span>
            </div>
            <p className="text-xs text-text-secondary mt-0.5 font-ibm">
              {isFreightForwarding
                ? 'International Air & Ocean Freight Forwarding inquiry aligned with Docket schema'
                : 'Domestic fleet assignment, surface route, and cargo specs'}
            </p>
          </div>
        </div>

        <Button
          data-testid="save-booking-btn"
          onClick={handleSubmit}
          disabled={saving}
          className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-10 px-6 rounded-lg shadow-sm flex items-center gap-2 cursor-pointer"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>{isEdit ? 'Update Booking' : 'Save Booking'}</span>
            </>
          )}
        </Button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 1: CUSTOMER & JOB DETAILS (HORIZONTAL FULL WIDTH)
         ═══════════════════════════════════════════════════════════════════════ */}
      <Card className="bg-white rounded-xl border border-gray-200/90 p-5 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-accent-blue" />
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm">
              {isFreightForwarding ? 'Job & Customer Information' : 'Customer Details'}
            </h3>
          </div>
          <span className="text-[11px] text-gray-400 font-ibm">
            Operating company determines booking category & docket schema
          </span>
        </div>

        {/* First Row of Customer Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-start">
          {/* Operating Company */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-slate-700 block font-ibm">
              Operating Company <span className="text-red-500">*</span>
            </Label>
            <select
              value={selectedCompanyId}
              onChange={e => {
                setSelectedCompanyId(e.target.value);
                setForm(f => ({ ...f, company_id: e.target.value }));
              }}
              className="flex h-9 w-full rounded-lg border border-accent-blue/40 bg-blue-50/30 px-3 py-1 text-xs font-bold text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
            >
              {companies.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code || (c.default_mode === 'FREIGHT_FORWARDING' ? 'FF' : 'LOGI')})
                </option>
              ))}
            </select>
          </div>

          {/* Inquiry Date */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-slate-700 block font-ibm">
              Inquiry Date <span className="text-red-500">*</span>
            </Label>
            <Input
              type="date"
              value={form.inquiry_date}
              onChange={e => setForm({ ...form, inquiry_date: e.target.value })}
              className="h-9 text-xs font-ibm rounded-lg border-gray-200 bg-white"
            />
          </div>

          {/* Booking Date */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-slate-700 block font-ibm">
              Booking Date <span className="text-red-500">*</span>
            </Label>
            <Input
              type="date"
              value={form.booking_date}
              onChange={e => setForm({ ...form, booking_date: e.target.value })}
              className="h-9 text-xs font-ibm rounded-lg border-gray-200 bg-white"
            />
          </div>

          {/* Customer / Bill To */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-slate-700 block font-ibm">
              {isFreightForwarding ? 'Bill To / Customer' : 'Customer'} <span className="text-red-500">*</span>
            </Label>
            <CustomerCombobox
              customers={customers}
              value={form.customer_id}
              selectedId={form.customer_id}
              onSelect={handleCustomerChange}
              placeholder="Search Customer..."
              className="w-full text-xs"
            />
          </div>

          {/* Customer Branch */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-slate-700 block font-ibm">
              Customer Branch
            </Label>
            <MasterCombobox
              items={branchOptions}
              value={form.customer_branch_id}
              selectedId={form.customer_branch_id}
              onSelect={handleBranchSelect}
              placeholder={branches.length ? 'Select Branch...' : 'No branches'}
              disabled={!form.customer_id || branches.length === 0}
              className="w-full text-xs"
            />
          </div>

          {/* Inward/Outward for Freight Forwarding OR Booking Type for Logistics */}
          {isFreightForwarding ? (
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700 block font-ibm">
                Inward / Outward <span className="text-red-500">*</span>
              </Label>
              <select
                value={ffData.inward_outward}
                onChange={e => setFfData({ ...ffData, inward_outward: e.target.value })}
                className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
              >
                <option value="Outward">Outward (Export)</option>
                <option value="Inward">Inward (Import)</option>
              </select>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700 block font-ibm">
                Booking Type
              </Label>
              <select
                value={form.booking_type}
                onChange={e => setForm({ ...form, booking_type: e.target.value })}
                className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
              >
                <option value="Online">Online</option>
                <option value="Direct">Direct</option>
                <option value="Agent">Agent</option>
                <option value="Partner">Partner</option>
              </select>
            </div>
          )}
        </div>

        {/* Second Row for Freight Forwarding (Shipper & Consignee) */}
        {isFreightForwarding && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2 border-t border-gray-100 items-start">
            {/* Shipper (Exporter) */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700 block font-ibm">
                Shipper (Exporter)
              </Label>
              <CustomerCombobox
                customers={customers}
                value={ffData.shipper_id}
                selectedId={ffData.shipper_id}
                onSelect={(id, cObj) => {
                  const c = (typeof id === 'object' && id !== null) ? id : (cObj || customers.find(x => String(x.id) === String(id)));
                  setFfData(prev => ({
                    ...prev,
                    shipper_id: c ? c.id : (id || ''),
                    shipper_name: c ? (c.business_name || c.name || c.company_name || '') : ''
                  }));
                }}
                placeholder="Search Shipper / Exporter..."
                className="w-full text-xs"
              />
            </div>

            {/* Consignee (Importer) */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700 block font-ibm">
                Consignee (Importer)
              </Label>
              <CustomerCombobox
                customers={customers}
                value={ffData.consignee_id}
                selectedId={ffData.consignee_id}
                onSelect={(id, cObj) => {
                  const c = (typeof id === 'object' && id !== null) ? id : (cObj || customers.find(x => String(x.id) === String(id)));
                  setFfData(prev => ({
                    ...prev,
                    consignee_id: c ? c.id : (id || ''),
                    consignee_name: c ? (c.business_name || c.name || c.company_name || '') : ''
                  }));
                }}
                placeholder="Search Consignee / Receiver..."
                className="w-full text-xs"
              />
            </div>

            {/* Booking Type */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700 block font-ibm">
                Booking Channel
              </Label>
              <select
                value={form.booking_type}
                onChange={e => setForm({ ...form, booking_type: e.target.value })}
                className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
              >
                <option value="Online">Online Customer Portal</option>
                <option value="Direct">Direct Client Inquiry</option>
                <option value="Agent">Freight Agent</option>
                <option value="Partner">Overseas Partner</option>
              </select>
            </div>
          </div>
        )}
      </Card>

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 2: FREIGHT FORWARDING SPECS vs DOMESTIC ROUTE & VEHICLE
         ═══════════════════════════════════════════════════════════════════════ */}
      {isFreightForwarding ? (
        <>
          {/* ─── FF Card A: Service & Cargo Classification (Docket Aligned) ─── */}
          <Card className="bg-white rounded-xl border border-gray-200/90 p-5 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-purple-600" />
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm">
                  Service & Cargo Classification
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFfData({ ...ffData, mode_of_transport: 'AIR' })}
                  className={`px-3 py-1 rounded-lg text-xs font-bold font-ibm flex items-center gap-1.5 transition-all cursor-pointer ${
                    ffData.mode_of_transport === 'AIR'
                      ? 'bg-violet-600 text-white shadow-xs'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Plane className="w-3.5 h-3.5" />
                  Air Freight
                </button>
                <button
                  type="button"
                  onClick={() => setFfData({ ...ffData, mode_of_transport: 'SEA' })}
                  className={`px-3 py-1 rounded-lg text-xs font-bold font-ibm flex items-center gap-1.5 transition-all cursor-pointer ${
                    ffData.mode_of_transport === 'SEA'
                      ? 'bg-teal-600 text-white shadow-xs'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Ship className="w-3.5 h-3.5" />
                  Ocean Freight
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-start">
              {/* Service Type */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Service <span className="text-red-500">*</span>
                </Label>
                <select
                  value={ffData.service_type}
                  onChange={e => setFfData({ ...ffData, service_type: e.target.value })}
                  className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
                >
                  <option value="Door To Door">Door To Door</option>
                  <option value="Door To Port">Door To Port</option>
                  <option value="Port To Port">Port To Port</option>
                  <option value="Port To Door">Port To Door</option>
                </select>
              </div>

              {/* Cargo Type */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Cargo Type <span className="text-red-500">*</span>
                </Label>
                <select
                  value={ffData.cargo_type}
                  onChange={e => setFfData({ ...ffData, cargo_type: e.target.value })}
                  className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
                >
                  <option value="General">General</option>
                  <option value="HAZ">HAZ (Dangerous Goods)</option>
                  <option value="HAZ Temp">HAZ Temp Controlled</option>
                  <option value="Non HAZ Temp">Non HAZ Temp Controlled</option>
                  <option value="Perishable">Perishable</option>
                  <option value="Valuable">Valuable Cargo</option>
                </select>
              </div>

              {/* Commercial Type */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Commercial Type
                </Label>
                <select
                  value={ffData.commercial_type}
                  onChange={e => setFfData({ ...ffData, commercial_type: e.target.value })}
                  className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
                >
                  <option value="Commercial">Commercial</option>
                  <option value="Non Commercial">Non Commercial</option>
                </select>
              </div>

              {/* Incoterms */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Incoterms
                </Label>
                <select
                  value={ffData.incoterms}
                  onChange={e => setFfData({ ...ffData, incoterms: e.target.value })}
                  className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
                >
                  <option value="FOB">FOB (Free On Board)</option>
                  <option value="CIF">CIF (Cost, Insurance & Freight)</option>
                  <option value="CFR">CFR (Cost & Freight)</option>
                  <option value="EXW">EXW (Ex Works)</option>
                  <option value="DAP">DAP (Delivered At Place)</option>
                  <option value="DDP">DDP (Delivered Duty Paid)</option>
                  <option value="FCA">FCA (Free Carrier)</option>
                  <option value="CIP">CIP (Carriage & Insurance Paid)</option>
                </select>
              </div>

              {/* Cargo Ready Date */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Cargo Ready Date
                </Label>
                <Input
                  type="date"
                  value={ffData.cargo_ready_date}
                  onChange={e => setFfData({ ...ffData, cargo_ready_date: e.target.value })}
                  className="h-9 text-xs font-ibm rounded-lg border-gray-200 bg-white"
                />
              </div>
            </div>

            {/* Conditional Temperature Requirement Text Field */}
            {['HAZ Temp', 'Non HAZ Temp', 'Perishable'].includes(ffData.cargo_type) && (
              <div className="p-3.5 bg-amber-50/70 border border-amber-200 rounded-xl space-y-1.5 transition-all">
                <div className="flex items-center gap-2">
                  <Thermometer className="w-4 h-4 text-amber-700" />
                  <Label className="text-xs font-bold text-amber-900 font-ibm">
                    Temperature Requirement <span className="text-red-500">*</span>
                  </Label>
                  <span className="text-[11px] text-amber-700 font-ibm">
                    (e.g., +2°C to +8°C, -20°C Deep Frozen, or 15°C to 25°C Ambient)
                  </span>
                </div>
                <Input
                  type="text"
                  placeholder="e.g. +2°C to +8°C"
                  value={ffData.temperature_requirement}
                  onChange={e => setFfData({ ...ffData, temperature_requirement: e.target.value })}
                  className="h-9 text-xs font-ibm font-medium rounded-lg border-amber-200 bg-white focus:ring-amber-500"
                />
              </div>
            )}
          </Card>

          {/* ─── FF Card B: Route & Shipment Info (POL, POD, Carrier, ETD/ETA) ─── */}
          <Card className="bg-white rounded-xl border border-gray-200/90 p-5 shadow-xs space-y-4">
            <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
              <MapPin className="w-4 h-4 text-purple-600" />
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm">
                Route & Shipment Info (Ports / Airports)
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-start">
              {/* POL / Loading From */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Loading From / POL <span className="text-red-500">*</span>
                </Label>
                <MasterCombobox
                  items={locationOptions}
                  value={form.from_location_id}
                  selectedId={form.from_location_id}
                  onSelect={id => {
                    const l = locations.find(x => x.id === id);
                    setForm(f => ({
                      ...f,
                      from_location_id: id,
                      from_location_name: l ? l.name : id
                    }));
                  }}
                  placeholder={ffData.mode_of_transport === 'AIR' ? 'Airport of Departure...' : 'Port of Loading (POL)...'}
                  className="w-full text-xs"
                />
              </div>

              {/* POD / Discharge At */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Discharge At / POD <span className="text-red-500">*</span>
                </Label>
                <MasterCombobox
                  items={locationOptions}
                  value={form.to_location_id}
                  selectedId={form.to_location_id}
                  onSelect={id => {
                    const l = locations.find(x => x.id === id);
                    setForm(f => ({
                      ...f,
                      to_location_id: id,
                      to_location_name: l ? l.name : id
                    }));
                  }}
                  placeholder={ffData.mode_of_transport === 'AIR' ? 'Airport of Destination...' : 'Port of Discharge (POD)...'}
                  className="w-full text-xs"
                />
              </div>

              {/* Carrier (Airline / Shipping Line) */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  {ffData.mode_of_transport === 'AIR' ? 'Preferred Airline' : 'Shipping Line'}
                </Label>
                <MasterCombobox
                  items={carrierOptions}
                  value={ffData.carrier_id}
                  selectedId={ffData.carrier_id}
                  onSelect={id => {
                    const c = carriers.find(x => x.id === id);
                    setFfData(prev => ({
                      ...prev,
                      carrier_id: id,
                      carrier_name: c ? (c.carrier_name || c.name) : id
                    }));
                  }}
                  placeholder="Select Carrier / Line..."
                  className="w-full text-xs"
                />
              </div>

              {/* Target Departure (ETD) */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Target ETD (Departure)
                </Label>
                <Input
                  type="date"
                  value={ffData.target_etd}
                  onChange={e => setFfData({ ...ffData, target_etd: e.target.value })}
                  className="h-9 text-xs font-ibm rounded-lg border-gray-200 bg-white"
                />
              </div>

              {/* Target Arrival (ETA) */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Target ETA (Arrival)
                </Label>
                <Input
                  type="date"
                  value={ffData.target_eta}
                  onChange={e => setFfData({ ...ffData, target_eta: e.target.value })}
                  className="h-9 text-xs font-ibm rounded-lg border-gray-200 bg-white"
                />
              </div>
            </div>
          </Card>

          {/* ─── FF Card C: Sea FCL Containers (only when Mode === 'SEA') ─── */}
          {ffData.mode_of_transport === 'SEA' && (
            <Card className="bg-white rounded-xl border border-gray-200/90 p-5 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                <div className="flex items-center gap-2">
                  <Anchor className="w-4 h-4 text-teal-600" />
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm">
                    Ocean Equipment / Container Requirements
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-ibm font-medium text-gray-500">Load Type:</span>
                  <button
                    type="button"
                    onClick={() => setFfData({ ...ffData, sea_shipment_type: 'FCL' })}
                    className={`px-2.5 py-0.5 rounded text-xs font-bold font-ibm cursor-pointer ${
                      ffData.sea_shipment_type === 'FCL' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    FCL (Full Container)
                  </button>
                  <button
                    type="button"
                    onClick={() => setFfData({ ...ffData, sea_shipment_type: 'LCL' })}
                    className={`px-2.5 py-0.5 rounded text-xs font-bold font-ibm cursor-pointer ${
                      ffData.sea_shipment_type === 'LCL' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    LCL (Consolidation)
                  </button>
                </div>
              </div>

              {ffData.sea_shipment_type === 'FCL' ? (
                <div className="space-y-3">
                  {containerItems.map((cItem, cIdx) => (
                    <div key={cItem.id || cIdx} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end bg-teal-50/30 p-3 rounded-xl border border-teal-100">
                      <div className="sm:col-span-7 space-y-1">
                        <Label className="text-xs font-bold text-teal-900 font-ibm">
                          Container Type & Size
                        </Label>
                        <select
                          value={cItem.container_type}
                          onChange={e => handleUpdateContainerItem(cIdx, 'container_type', e.target.value)}
                          className="h-9 w-full rounded-lg border border-teal-200 bg-white px-3 text-xs font-medium font-ibm shadow-xs"
                        >
                          <option value="20' Standard">20' Standard (20' GP)</option>
                          <option value="40' Standard">40' Standard (40' GP)</option>
                          <option value="40' High Cube">40' High Cube (40' HC)</option>
                          <option value="20' Reefer">20' Reefer</option>
                          <option value="40' Reefer">40' Reefer</option>
                          <option value="45' High Cube">45' High Cube</option>
                          <option value="20' Open Top">20' Open Top</option>
                          <option value="40' Open Top">40' Open Top</option>
                          <option value="20' Flat Rack">20' Flat Rack</option>
                          <option value="40' Flat Rack">40' Flat Rack</option>
                        </select>
                      </div>

                      <div className="sm:col-span-4 space-y-1">
                        <Label className="text-xs font-bold text-teal-900 font-ibm">
                          Number of Containers
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          value={cItem.no_of_containers}
                          onChange={e => handleUpdateContainerItem(cIdx, 'no_of_containers', parseInt(e.target.value, 10) || 1)}
                          className="h-9 text-xs font-ibm bg-white border-teal-200"
                        />
                      </div>

                      <div className="sm:col-span-1 flex items-center justify-center pb-1">
                        {containerItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveContainerRow(cIdx)}
                            className="text-red-500 hover:text-red-700 p-1.5 transition-colors cursor-pointer"
                            title="Remove container type"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddContainerRow}
                    className="border-dashed border-teal-300 text-teal-700 hover:bg-teal-50 text-xs font-ibm font-semibold h-8 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    + Add Container Type
                  </Button>
                </div>
              ) : (
                <div className="p-3 bg-teal-50/50 rounded-xl border border-teal-100 text-xs text-teal-800 font-ibm">
                  LCL (Less than Container Load) will be computed directly based on package line items, gross weight, and cubic volume (CBM) below.
                </div>
              )}
            </Card>
          )}
        </>
      ) : (
        /* ═══════════════════════════════════════════════════════════════════════
            DOMESTIC LOGISTICS SECTIONS (ROUTE & VEHICLE SPECS)
           ═══════════════════════════════════════════════════════════════════════ */
        <>
          {/* ─── Route & Load (Horizontal Full Width) ─── */}
          <Card className="bg-white rounded-xl border border-gray-200/90 p-5 shadow-xs space-y-4">
            <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
              <MapPin className="w-4 h-4 text-accent-blue" />
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm">
                Route & Load
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
              {/* From */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  From <span className="text-red-500">*</span>
                </Label>
                <MasterCombobox
                  items={locationOptions}
                  value={form.from_location_id}
                  selectedId={form.from_location_id}
                  onSelect={id => {
                    const l = locations.find(x => x.id === id);
                    setForm(f => ({
                      ...f,
                      from_location_id: id,
                      from_location_name: l ? l.name : id
                    }));
                  }}
                  placeholder="Select Origin..."
                  className="w-full text-xs"
                />
              </div>

              {/* To */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  To <span className="text-red-500">*</span>
                </Label>
                <MasterCombobox
                  items={locationOptions}
                  value={form.to_location_id}
                  selectedId={form.to_location_id}
                  onSelect={id => {
                    const l = locations.find(x => x.id === id);
                    setForm(f => ({
                      ...f,
                      to_location_id: id,
                      to_location_name: l ? l.name : id
                    }));
                  }}
                  placeholder="Select Destination..."
                  className="w-full text-xs"
                />
              </div>

              {/* Load Type */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Load Type
                </Label>
                <select
                  value={form.load_type}
                  onChange={e => setForm({ ...form, load_type: e.target.value })}
                  className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
                >
                  <option value="">--Select--</option>
                  <option value="FTL">FTL (Full Truck Load)</option>
                  <option value="PTL">PTL (Part Truck Load)</option>
                  <option value="Parcel">Parcel</option>
                  <option value="Bulk">Bulk</option>
                  <option value="Temp Controlled">Temp Controlled</option>
                </select>
              </div>

              {/* Domestic Freight Term */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Freight Term
                </Label>
                <select
                  value={form.freight_term || ffData.incoterms || 'PAID'}
                  onChange={e => {
                    const val = e.target.value;
                    setForm(f => ({ ...f, freight_term: val }));
                    setFfData(prev => ({ ...prev, incoterms: val }));
                  }}
                  className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
                >
                  <option value="PAID">PAID (Prepaid)</option>
                  <option value="FOD">FOD (Freight on Delivery)</option>
                  <option value="TBB">TBB (To Be Billed)</option>
                </select>
              </div>
            </div>
          </Card>

          {/* ─── Vehicle Specs (Horizontal Full Width) ─── */}
          <Card className="bg-white rounded-xl border border-gray-200/90 p-5 shadow-xs space-y-4">
            <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
              <Truck className="w-4 h-4 text-accent-blue" />
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm">
                Vehicle & Fleet Specs
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
              {/* Vehicle Type */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Vehicle Type
                </Label>
                <select
                  value={form.vehicle_type_id || ''}
                  onChange={e => handleVehicleTypeChange(e.target.value)}
                  className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue font-ibm"
                >
                  <option value="">-- Select Type --</option>
                  {vehicleTypes.map(vt => (
                    <option key={vt.id} value={vt.id}>
                      {vt.type_name || vt.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* No. of Trucks */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  No. of Trucks
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={form.no_of_vehicles}
                  onChange={e => handleNumTrucksChange(e.target.value)}
                  className="h-9 text-xs font-ibm rounded-lg border-gray-200 bg-white"
                />
              </div>

              {/* Trip Type */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Trip Type
                </Label>
                <select
                  value={form.trip_type}
                  onChange={e => setForm({ ...form, trip_type: e.target.value })}
                  className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
                >
                  <option value="">--Select--</option>
                  <option value="Single Trip">Single Trip</option>
                  <option value="Round Trip">Round Trip</option>
                </select>
              </div>

              {/* Size / Capacity */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 block font-ibm">
                  Size / Capacity
                </Label>
                <select
                  value={form.vehicle_size}
                  onChange={e => setForm({ ...form, vehicle_size: e.target.value })}
                  className="flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-accent-blue"
                >
                  <option value="">-- Select Size --</option>
                  {availableVehicleSizes.map((sz, idx) => (
                    <option key={`${sz}-${idx}`} value={sz}>
                      {sz}
                    </option>
                  ))}
                  {form.vehicle_size && !availableVehicleSizes.includes(form.vehicle_size) && (
                    <option value={form.vehicle_size}>{form.vehicle_size}</option>
                  )}
                </select>
                <Input
                  type="text"
                  placeholder="Or type custom size (e.g. 32 FT)..."
                  value={form.vehicle_size}
                  onChange={e => setForm({ ...form, vehicle_size: e.target.value })}
                  className="h-9 text-xs font-ibm rounded-lg border-gray-200 bg-white mt-1.5"
                />
              </div>
            </div>

            {/* Multi-truck allocations */}
            {form.no_of_vehicles > 1 && (
              <div className="pt-3 border-t border-gray-100">
                <Label className="text-xs font-bold text-slate-700 block mb-2 font-ibm">
                  Individual Truck Capacity Allocation ({form.no_of_vehicles} Trucks)
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                  {truckSizes.map((t, idx) => (
                    <div key={t.id} className="space-y-1 bg-gray-50/70 p-2.5 rounded-lg border border-gray-200">
                      <span className="text-[11px] font-bold text-slate-700 font-ibm block">
                        {t.label}
                      </span>
                      <select
                        value={t.size || ''}
                        onChange={e => handleTruckSizeChange(idx, e.target.value)}
                        className="flex h-8 w-full rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-slate-800 shadow-xs focus:outline-none focus:ring-1 focus:ring-accent-blue font-ibm"
                      >
                        <option value="">-- Select Size --</option>
                        {availableVehicleSizes.map((sz, sIdx) => (
                          <option key={`${sz}-${sIdx}`} value={sz}>
                            {sz}
                          </option>
                        ))}
                        {t.size && !availableVehicleSizes.includes(t.size) && (
                          <option value={t.size}>{t.size}</option>
                        )}
                      </select>
                      <Input
                        type="text"
                        placeholder="Custom size..."
                        value={t.size}
                        onChange={e => handleTruckSizeChange(idx, e.target.value)}
                        className="h-7 text-[11px] font-ibm bg-white border-gray-200"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 3: CARGO PACKAGES & DIMENSIONS (FULL WIDTH CARD)
         ═══════════════════════════════════════════════════════════════════════ */}
      <Card className="bg-white rounded-xl border border-gray-200/90 p-5 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
          <div className="flex items-center gap-2">
            <Box className="w-4 h-4 text-accent-blue" />
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm">
              {isFreightForwarding ? 'Package Info & Dimensions (Cargo Items)' : 'Cargo Packages & Dimensions'}
            </h3>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddPackageRow}
            className="border-dashed border-accent-blue/50 text-accent-blue hover:bg-blue-50/50 text-xs font-ibm font-semibold h-8 cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            + Add Package Line
          </Button>
        </div>

        {/* Dynamic Package Rows */}
        <div className="space-y-3">
          {packageItems.map((item, idx) => (
            <div
              key={item.id || idx}
              className="grid grid-cols-12 gap-3 items-end bg-gray-50/60 p-3 rounded-xl border border-gray-200/80 transition-all"
            >
              {/* Package Type */}
              <div className="col-span-12 sm:col-span-2 space-y-1">
                <Label className="text-[11px] font-bold text-gray-600 block font-ibm">
                  Package Type
                </Label>
                <select
                  value={item.package_type || 'Boxes'}
                  onChange={e => handleUpdatePackageItem(idx, 'package_type', e.target.value)}
                  className="h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-xs font-medium font-ibm shadow-xs focus:ring-2 focus:ring-accent-blue"
                >
                  <option value="Boxes">Boxes</option>
                  <option value="Pallets">Pallets</option>
                  <option value="Drums">Drums</option>
                  <option value="Cartons">Cartons</option>
                  <option value="Crates">Crates</option>
                  <option value="Wooden Cases">Wooden Cases</option>
                  <option value="Bags">Bags</option>
                  <option value="Rolls">Rolls</option>
                  <option value="Loose">Loose</option>
                </select>
              </div>

              {/* No. of Packages */}
              <div className="col-span-6 sm:col-span-2 space-y-1">
                <Label className="text-[11px] font-bold text-gray-600 block font-ibm">
                  Packages <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="e.g. 10"
                  value={item.packages}
                  onChange={e => handleUpdatePackageItem(idx, 'packages', e.target.value)}
                  className="h-9 text-xs font-ibm bg-white border-gray-200"
                />
              </div>

              {/* Gross Weight (Kg) */}
              <div className="col-span-6 sm:col-span-2 space-y-1">
                <Label className="text-[11px] font-bold text-gray-600 block font-ibm">
                  Gross Wt (Kg) <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  step="any"
                  placeholder="e.g. 500"
                  value={item.gross_weight}
                  onChange={e => handleUpdatePackageItem(idx, 'gross_weight', e.target.value)}
                  className="h-9 text-xs font-ibm bg-white border-gray-200"
                />
              </div>

              {/* Dimensions (L x W x H) */}
              <div className="col-span-12 sm:col-span-4 space-y-1">
                <Label className="text-[11px] font-bold text-gray-600 block font-ibm">
                  Dimensions (L × W × H)
                </Label>
                <div className="grid grid-cols-3 gap-1.5">
                  <Input
                    type="number"
                    step="any"
                    placeholder="L"
                    value={item.length || ''}
                    onChange={e => handleUpdatePackageItem(idx, 'length', e.target.value)}
                    className="h-9 text-xs font-ibm bg-white border-gray-200"
                  />
                  <Input
                    type="number"
                    step="any"
                    placeholder="W"
                    value={item.width || ''}
                    onChange={e => handleUpdatePackageItem(idx, 'width', e.target.value)}
                    className="h-9 text-xs font-ibm bg-white border-gray-200"
                  />
                  <Input
                    type="number"
                    step="any"
                    placeholder="H"
                    value={item.height || ''}
                    onChange={e => handleUpdatePackageItem(idx, 'height', e.target.value)}
                    className="h-9 text-xs font-ibm bg-white border-gray-200"
                  />
                </div>
              </div>

              {/* Unit */}
              <div className="col-span-6 sm:col-span-1 space-y-1">
                <Label className="text-[11px] font-bold text-gray-600 block font-ibm">
                  Unit
                </Label>
                <select
                  value={item.unit || 'CM'}
                  onChange={e => handleUpdatePackageItem(idx, 'unit', e.target.value)}
                  className="h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-xs font-semibold font-ibm shadow-xs"
                >
                  <option value="CM">CM</option>
                  <option value="Inches">IN</option>
                </select>
              </div>

              {/* Delete action */}
              <div className="col-span-6 sm:col-span-1 flex items-center justify-center pb-1">
                {packageItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemovePackageRow(idx)}
                    className="text-red-500 hover:text-red-700 p-1.5 transition-colors cursor-pointer"
                    title="Remove package line"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Total Cargo Summary Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between bg-blue-50/70 border border-blue-100 rounded-xl px-4 py-3 text-xs font-ibm mt-2">
          <div className="flex items-center gap-2">
            <span className="font-bold text-blue-900">Total Cargo Summary:</span>
            <span className="text-[11px] text-blue-700">
              ({packageItems.length} {packageItems.length === 1 ? 'line item' : 'line items'})
            </span>
          </div>

          <div className="flex flex-wrap gap-4 sm:gap-6 text-blue-800 font-semibold mt-2 sm:mt-0">
            <span>
              Total Packages: <strong className="text-blue-950 font-bold">{totalPackages}</strong>
            </span>
            <span>
              Total Gross Weight: <strong className="text-blue-950 font-bold">{totalGrossWeight} Kg</strong>
            </span>
            {isFreightForwarding && (
              <>
                <span>
                  Total Volume: <strong className="text-blue-950 font-bold">{totalCBM.toFixed(3)} CBM</strong>
                </span>
                <span>
                  Volumetric Wt: <strong className="text-blue-950 font-bold">{totalVolumetricWeight.toFixed(2)} Kg</strong>
                </span>
                <span className="bg-blue-600 text-white px-2 py-0.5 rounded font-bold">
                  Chargeable Wt: {chargeableWeight.toFixed(2)} Kg
                </span>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 4: REMARKS / SPECIAL INSTRUCTIONS
         ═══════════════════════════════════════════════════════════════════════ */}
      <Card className="bg-white rounded-xl border border-gray-200/90 p-5 shadow-xs space-y-3">
        <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
          <FileText className="w-4 h-4 text-accent-blue" />
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm">
            {isFreightForwarding ? 'Special Handling Instructions & Remarks' : 'Remarks & Instructions'}
          </h3>
        </div>
        <Textarea
          rows={3}
          placeholder={isFreightForwarding
            ? 'Any customs clearance notes, HazMat UN numbers / Class, temperature logger instructions, or airline/vessel routing preferences...'
            : 'Special instructions, delivery remarks, landmark, or handling requirements...'}
          value={form.remarks}
          onChange={e => setForm({ ...form, remarks: e.target.value })}
          className="text-xs font-ibm rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-accent-blue bg-white"
        />
      </Card>
    </div>
  );
};

export default BookingRequestForm;
